"use client";

import { useState, useEffect, useRef } from "react";
import { useScrollAnimate } from "../lib/hooks";

const TEXT = "#EDEDED";
const TEAL = "#4FCDC4";
const MK_BG = "#0f0f11";
const MK_CARD = "rgba(255,255,255,0.04)";
const MK_BD = "rgba(255,255,255,0.06)";
const MK_TEXT = "#a0a0a0";

export default function ChatGPTAdMockup() {
  const [ref, scrolled] = useScrollAnimate();
  const PROMPT = "What's the best AEO software for my website?";
  const RESPONSE = "There are several AEO platforms designed to help optimize for AI search. Here are some worth considering...";
  const RESPONSE_WORDS = RESPONSE.split(" ");
  const [charCount, setCharCount] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [phase, setPhase] = useState("idle");
  const animRef = useRef(false);

  useEffect(() => {
    if (!scrolled || animRef.current) return;
    animRef.current = true;
    const t1 = setTimeout(() => {
      setPhase("typing");
      let i = 0;
      const interval = setInterval(() => {
        i++;
        setCharCount(i);
        if (i >= PROMPT.length) {
          clearInterval(interval);
          setTimeout(() => {
            setPhase("sent");
            setTimeout(() => {
              setPhase("response");
              let w = 0;
              const wordInterval = setInterval(() => {
                w++;
                setWordCount(w);
                if (w >= RESPONSE_WORDS.length) {
                  clearInterval(wordInterval);
                  setTimeout(() => setPhase("ad"), 600);
                }
              }, 60);
            }, 500);
          }, 400);
        }
      }, 35);
    }, 300);
    return () => clearTimeout(t1);
  }, [scrolled]);

  const isTyping = phase === "typing";
  const isSent = phase === "sent" || phase === "response" || phase === "ad";
  const showResponse = phase === "response" || phase === "ad";
  const showAd = phase === "ad";

  return (
    <div style={{ margin: "28px 0" }}>
      <div ref={ref} style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
        <div style={{
          width: "100%", maxWidth: 300, height: 540, borderRadius: 24, overflow: "hidden",
          border: `1px solid ${MK_BD}`, boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
          background: MK_BG, display: "flex", flexDirection: "column",
        }}>
          {/* App header */}
          <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${MK_BD}` }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/></svg>
            <span style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>ChatGPT</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
          </div>

          {/* Chat content */}
          <div style={{ padding: "16px 16px 0", display: "flex", flexDirection: "column", flex: 1 }}>
            <div style={{ flex: 1 }}>
              {!isSent ? (
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", minHeight: 200 }}>
                  {!isTyping && phase === "idle" && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 600, color: TEXT }}>What can I help with?</div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* User message */}
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                    <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 16, padding: "10px 14px", maxWidth: "80%" }}>
                      <span style={{ fontSize: 13, color: TEXT }}>{PROMPT}</span>
                    </div>
                  </div>

                  {/* Response */}
                  <div style={{ opacity: showResponse ? 1 : 0, transition: "opacity 0.2s ease" }}>
                    <div style={{ fontSize: 13, color: MK_TEXT, lineHeight: 1.7, marginBottom: 6 }}>
                      {RESPONSE_WORDS.slice(0, wordCount).join(" ")}
                      {showResponse && wordCount < RESPONSE_WORDS.length && (
                        <span style={{ display: "inline-block", width: 6, height: 14, background: MK_TEXT, borderRadius: 1, marginLeft: 2, verticalAlign: "text-bottom", opacity: 0.5 }} />
                      )}
                    </div>
                  </div>

                  {/* Sponsored ad slides in */}
                  <div style={{
                    opacity: showAd ? 1 : 0,
                    transform: showAd ? "translateY(0)" : "translateY(10px)",
                    transition: "opacity 0.6s ease, transform 0.6s ease",
                    borderTop: `1px solid ${MK_BD}`, paddingTop: 12, marginTop: 4,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#000", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 14, color: TEAL, fontWeight: 700 }}>&#10022;</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: TEXT, lineHeight: 1.2 }}>AI-Advisors</div>
                        <div style={{ fontSize: 11, color: MK_TEXT }}>Sponsored</div>
                      </div>
                    </div>
                    <div style={{
                      background: MK_CARD, borderRadius: 10,
                      padding: "12px 14px", border: `1px solid ${MK_BD}`, display: "inline-flex", gap: 12, alignItems: "center",
                      maxWidth: "85%",
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: TEXT, marginBottom: 2 }}>Best AEO Software</div>
                        <div style={{ fontSize: 11, color: MK_TEXT, lineHeight: 1.4 }}>Track, analyze, and</div>
                        <div style={{ fontSize: 11, color: MK_TEXT, lineHeight: 1.4 }}>grow your AI visibility</div>
                      </div>
                      <div style={{ width: 48, height: 48, borderRadius: 8, background: "rgba(79,205,196,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="36" height="36" viewBox="0 0 36 36">
                          <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
                          <circle cx="18" cy="18" r="14" fill="none" stroke={TEAL} strokeWidth="3"
                            strokeDasharray={`${0.92 * 2 * Math.PI * 14} ${2 * Math.PI * 14}`}
                            strokeLinecap="round" transform="rotate(-90 18 18)" />
                          <text x="18" y="19" textAnchor="middle" dominantBaseline="central"
                            style={{ fontSize: 9, fontWeight: 700, fill: TEXT, fontFamily: "var(--mono)" }}>92</text>
                        </svg>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Input bar */}
            <div style={{
              marginTop: 12, marginBottom: 16,
              background: "rgba(255,255,255,0.06)", borderRadius: 22, padding: "10px 12px",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ fontSize: 13, color: isTyping ? TEXT : MK_TEXT, flex: 1, fontFamily: "var(--body)" }}>
                {isTyping ? PROMPT.slice(0, charCount) : "Ask anything"}
                {isTyping && <span style={{ borderRight: "2px solid rgba(255,255,255,0.6)", marginLeft: 1 }}>&nbsp;</span>}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
